import { useCallback, useEffect, useMemo } from "react";
import { captureException } from "@sentry/react";
import { ChatBoxFileUpload, ChatComponent } from "../ChatBox";
import {
  useLazyQuery,
  useMutation,
  useSubscription,
  useQuery,
} from "@apollo/client";
import { ChatToolType, PageContext } from "_graphql-types-frontend/graphql";
import {
  AGENT_CHAT,
  CONTEXTUAL_CHAT_SUBSCRIPTION,
  STOP_CHAT,
  GET_KNOWLEDGE_BASE,
  LLM_MODELS,
  servesChat,
} from "./graphql";
import {
  RangerChatMenuButton,
  RangerChatSettings,
} from "./RangerChatMenu";
import {
  useLoadConversation,
  usePendingConversation,
  useRecoverTurn,
} from "./useLoadConversation";
import { ConversationHistoryTab } from "./menu/ConversationHistoryTab";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "../Typography/Typography";
import { useRefetchTemplateFieldsOnFieldWrite } from "../Template/useRefetchTemplateFieldsOnFieldWrite";
import * as types from "_graphql-types-frontend/graphql";
import {
  useRangerChatStore,
  mcpExclusionVariables,
  selectSessionId,
  selectChatHistory,
  selectUserText,
  selectConversationId,
  selectQueuedMessage,
  selectShouldStop,
  selectIsWaitingForResponse,
} from "./chatStore";
import { ChatComponentFiles } from "../ChatBox/stores";
import {
  ChatMessage,
  ToolCallEntry,
  MarkdownWithToolsMessage,
  ToolsReactMessage,
} from "../ChatBox/types";
import { applyStreamEventToHistory } from "./streamEventToHistory";
import { SlotWatcher } from "./SlotWatcher";
import { ChatNotificationPrompt } from "./ChatNotificationPrompt";
import { sanitizeHtmlForLLM } from "./sanitizeHtmlForLLM";
import { useAvailableToolsDisplay } from "./availableTools";
import { dispatchMessage, releaseQueuedMessage, withoutQueuedMessage } from "./dispatchMessage";
import { portfolioIdFromParams } from "./portfolioContext";
/**
 * The spinner's status line: what the agent is doing during an otherwise
 * silent pause instead of a bare "Working…". Repeat compactions are numbered:
 * one is routine, several in a row is a turn struggling to fit.
 */
function chatActivityLabel({
  retryNote,
  isEditingContext,
  isCompressing,
  compressionCount,
}: {
  retryNote?: string;
  isEditingContext: boolean;
  isCompressing: boolean;
  compressionCount: number;
}): string {
  if (retryNote) return retryNote;
  if (isEditingContext) return "Freeing memory…";
  if (isCompressing && compressionCount > 1) return `Compressing context… (${compressionCount})`;
  if (isCompressing) return "Compressing context…";
  return "Working…";
}

export const RangerChatStoreId = "ranger-chat";

// SlotWatcher is extracted to its own module for unit testing. See
// SlotWatcher.tsx for its full docstring + behavior, and SlotWatcher.unit.test
// for race-condition and lifecycle coverage.

export function RangerChat({
  params,
  isFullscreen,
}: {
  params?: {
    [key: string]: number | string | undefined;
  };
  isFullscreen?: boolean;
}) {
  // Per-slot reads via narrowly-targeted selectors so each one only re-renders
  // when its specific field on the active slot changes.
  const slots = useRangerChatStore(s => s.slots);
  const sessionId = useRangerChatStore(selectSessionId);
  const chatHistory = useRangerChatStore(selectChatHistory);

  // An agent field write does not go through a mutation plan, so there is no
  // approval moment to refresh off — watch the stream and pull the Diligence
  // template as soon as a write lands.
  useRefetchTemplateFieldsOnFieldWrite(chatHistory);
  const userText = useRangerChatStore(selectUserText);
  const conversationId = useRangerChatStore(selectConversationId);
  const queuedMessage = useRangerChatStore(selectQueuedMessage);
  const shouldStop = useRangerChatStore(selectShouldStop);
  const isWaitingForResponse = useRangerChatStore(selectIsWaitingForResponse);
  const isCompressing = useRangerChatStore(s => s.slots.get(s.activeId)?.isCompressing ?? false);
  const isEditingContext = useRangerChatStore(s => s.slots.get(s.activeId)?.isEditingContext ?? false);
  const compressionCount = useRangerChatStore(s => s.slots.get(s.activeId)?.compressionCount ?? 0);
  const retryNote = useRangerChatStore(s => s.slots.get(s.activeId)?.retryNote);
  const activityLabel = chatActivityLabel({ retryNote, isEditingContext, isCompressing, compressionCount });

  // Setters: per-slot mutators target the active slot via patchActiveSlot,
  // wrapped in stable callbacks so dependents don't re-render on every patch.
  const patchActiveSlot = useRangerChatStore(s => s.patchActiveSlot);
  const setSlotStatus = useRangerChatStore(s => s.setSlotStatus);
  const patchSlot = useRangerChatStore(s => s.patchSlot);
  const appendToActiveChatHistory = useRangerChatStore(s => s.appendToActiveChatHistory);
  const setActiveChatHistory = useRangerChatStore(s => s.setActiveChatHistory);
  const appendToSlotChatHistory = useRangerChatStore(s => s.appendToSlotChatHistory);
  const setChatHistory = setActiveChatHistory;
  const addToChatHistory = appendToActiveChatHistory;
  const setUserText = useCallback((text: string) => patchActiveSlot({ userText: text }), [patchActiveSlot]);
  const setConversationId = useCallback((id: number | null) => patchActiveSlot({ conversationId: id }), [patchActiveSlot]);
  const setShouldStop = useCallback((flag: boolean) => patchActiveSlot({ shouldStop: flag }), [patchActiveSlot]);

  // Slot management
  const newChat = useRangerChatStore(s => s.newChat);
  const appendToQueuedMessage = useRangerChatStore(s => s.appendToQueuedMessage);
  const consumeQueuedMessage = useRangerChatStore(s => s.consumeQueuedMessage);
  const editQueuedMessage = useRangerChatStore(s => s.editQueuedMessage);
  const clearQueuedMessage = useRangerChatStore(s => s.clearQueuedMessage);
  const loadConversationIntoChat = useLoadConversation();
  const recoverTurn = useRecoverTurn();
  const autoSendMessage = useRangerChatStore(s => s.autoSendMessage);
  const consumeAutoSendMessage = useRangerChatStore(s => s.consumeAutoSendMessage);

  // Every slot we want a live subscription for. The active slot is ALWAYS
  // included so the subscription is ready before the user types & sends —
  // closing the race where the first stream event (carrying the convId) could
  // be missed during a slow Apollo subscribe. Slots in pending/generating are
  // also watched so a background chat keeps streaming after the user
  // navigates away. The keys are stable sessionIds, so React preserves each
  // watcher across active-slot changes.
  const watchedSessions = useMemo(() => {
    const set = new Set<string>();
    set.add(sessionId);
    for (const slot of slots.values()) {
      if (slot.status === "pending" || slot.status === "generating") {
        set.add(slot.sessionId);
      }
    }
    return [...set];
  }, [slots, sessionId]);

  const disabledTools = useRangerChatStore(s => s.disabledTools);
  const setDisabledTools = useRangerChatStore(s => s.setDisabledTools);

  const knowledgeBaseId = useRangerChatStore(s => s.knowledgeBaseId);
  const setKnowledgeBaseId = useRangerChatStore(s => s.setKnowledgeBaseId);

  const storedModel = useRangerChatStore(s => s.model);
  const { data: modelsData } = useQuery(LLM_MODELS, { fetchPolicy: "cache-first" });
  // A stored key the server does not offer for chat (inactive, sub-agent
  // only, or from another environment) is dropped, so the server runs its default instead of
  // refusing the turn. Until the list loads the stored pick is sent as-is;
  // the server still refuses a key it does not allow.
  const models = modelsData?.llmModels;
  const model =
    !models || models.some(m => m.key === storedModel && servesChat(m))
      ? storedModel
      : null;
  const effort = useRangerChatStore(s => s.effort);
  const excludedMcpCategories = useRangerChatStore(s => s.excludedMcpCategories);

  // Auto-enable documents, notes, citations, and MCP tools when knowledge base is attached
  useEffect(() => {
    if (knowledgeBaseId) {
      const toolsToEnable = [
        types.ChatToolType.DocumentsRagTool,
        types.ChatToolType.NotesRagTool,
        types.ChatToolType.CitationTool,
        types.ChatToolType.SiriusMcp,
      ];

      // Remove these tools from disabled list if they're there
      setDisabledTools(
        (disabledTools || []).filter(tool => !toolsToEnable.includes(tool))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- disabledTools intentionally excluded to avoid infinite loop
  }, [knowledgeBaseId, setDisabledTools]);

  // Fetch knowledge base info when ID is set
  const { data: knowledgeBaseData, refetch: refetchKnowledgeBase } = useQuery(
    GET_KNOWLEDGE_BASE,
    {
      variables: { id: knowledgeBaseId! },
      skip: !knowledgeBaseId,
      pollInterval: 5000, // Poll every 5 seconds to check status
    }
  );

  const getCurrentPageContext = useCallback(() => {
    const sanitizedHtml = sanitizeHtmlForLLM();

    const contentTags = Array.from(
      new DOMParser()
        .parseFromString(sanitizedHtml, "text/html")
        .querySelectorAll("[data-content]")
    ).map(el => el.getAttribute("data-content"));

    return {
      url: window.location.href,
      params: JSON.stringify(params),
      items: [
        {
          data: sanitizedHtml,
          type: types.PageContentType.Html,
          id: "page_context",
          anchor: null,
          description: ["Current Page Content", ...contentTags].join(", "),
        },
      ],
    } as PageContext;
  }, [params]);

  const [entityId, entityType] = useMemo(() => {
    if (!params) {
      return [undefined, undefined];
    }
    if (params.investmentId) {
      return [Number(params.investmentId), types.ChatEntityType.Investment];
    } else if (params.companyId) {
      return [Number(params.companyId), types.ChatEntityType.Company];
    } else if (params.firmId) {
      return [Number(params.firmId), types.ChatEntityType.Firm];
    }
    return [undefined, undefined];
  }, [params]);

  // The Asset Allocation page's portfolio, as its own context field (not an
  // entity type: see portfolioContext.ts).
  const portfolioId = useMemo(() => portfolioIdFromParams(params), [params]);

  const availableToolsDisplay = useAvailableToolsDisplay();

  // No onError handler here — error handling is consolidated into
  // dispatchMessage's .catch so it can target `sentSessionId` (the slot
  // the user actually sent from), not whichever slot they happen to be
  // looking at now. Apollo's onError fires from a context that doesn't
  // have access to the captured sessionId, which made it impossible to
  // route the error message to the right slot.
  const [sendChat] = useLazyQuery(AGENT_CHAT, {
    fetchPolicy: "no-cache",
  });

  const [stopChat] = useMutation(STOP_CHAT);

  const cancelAgentFocus = useCallback(() => {
    setShouldStop(true);
    clearQueuedMessage();
    setSlotStatus(sessionId, "done");
    stopChat({ variables: { sessionId } })
      .then(() => {
        addToChatHistory({
          type: "agent",
          data: {
            type: "markdown",
            message: "_Response stopped by user_",
          },
        });
      })
      .catch(err => {
        captureException(err, { tags: { feature: "rangerChat:stopChat" } });
      })
      .finally(() => {
        setShouldStop(false);
      });
  }, [sessionId, setShouldStop, clearQueuedMessage, stopChat, addToChatHistory, setSlotStatus]);

  // Active-slot subscription is handled by the SlotWatcher mounted for
  // sessionId below. We deliberately don't have a separate top-level
  // useSubscription here — a single watcher per slot (with a stable key)
  // avoids the unmount/remount race that loses convId events when the user
  // switches conversations mid-stream.

  const dispatchMessageCb = useCallback((messageText: string, files?: ChatComponentFiles[]) => {
    dispatchMessage(
      {
        state: useRangerChatStore.getState(),
        sendChat: sendChat as any,
        getSlot: sid => useRangerChatStore.getState().slots.get(sid),
        recoverTurn,
        actions: {
          patchSlot,
          setSlotStatus,
          appendToActiveChatHistory,
          appendToSlotChatHistory,
        },
      },
      {
        messageText,
        buildVariables: ({ sessionId: sid, conversationId: cid, messageText: msg }) => ({
          question: msg,
          conversationId: cid,
          chatContext: {
            entityId,
            entityType,
            portfolioId,
            knowledgeBaseId: knowledgeBaseId ?? undefined,
            pageContext: getCurrentPageContext(),
            files: (files || [])
              .map(({ url, bucket, key, name, documentId, documentQueueId }) => ({
                fileUrl: url,
                bucket: bucket,
                key: key,
                name: name,
                ...(documentId ? { documentId } : {}),
                ...(documentQueueId ? { documentQueueId } : {}),
              }))
              .filter(f => f.fileUrl && f.bucket && f.key && f.name) as types.ChatContextFile[],
          },
          tools: availableToolsDisplay
            .map(tool => tool.tool)
            .filter(tool => !disabledTools?.includes(tool)),
          sessionId: sid,
          model,
          effort,
          ...mcpExclusionVariables(excludedMcpCategories),
        } as any),
      }
    );
  }, [
    sendChat, model, effort, excludedMcpCategories, entityId, entityType, portfolioId, knowledgeBaseId,
    getCurrentPageContext, availableToolsDisplay, disabledTools,
    appendToActiveChatHistory, appendToSlotChatHistory, patchSlot, setSlotStatus, recoverTurn,
  ]);

  // Send the queued message when response completes. A new chat whose first
  // turn failed has no conversation to continue, so the queued text goes back
  // into the input instead of waiting there unsent (issue 5552).
  useEffect(() => {
    if (!queuedMessage) return;
    releaseQueuedMessage({
      isWaitingForResponse,
      conversationId,
      consumeQueuedMessage,
      send: dispatchMessageCb,
      restore: message => {
        setChatHistory((history: ChatMessage[]) => withoutQueuedMessage(history, message));
        setUserText(userText ? `${userText}\n${message}` : message);
      },
    });
  }, [isWaitingForResponse, queuedMessage, conversationId, consumeQueuedMessage, dispatchMessageCb, setUserText, setChatHistory, userText]);

  // Something outside the chat asked to open a conversation — today the
  // notification bell. Consumed here, in the panel itself, rather than in the
  // history tab: that tab only exists while the chat's settings popover is
  // open, so a notification link did nothing whenever it wasn't.
  usePendingConversation(loadConversationIntoChat);

  // Auto-send messages triggered by UI actions (e.g., "Ask agent to fix" button)
  useEffect(() => {
    if (!isWaitingForResponse && autoSendMessage) {
      const msg = consumeAutoSendMessage();
      if (msg) {
        dispatchMessageCb(msg);
      }
    }
  }, [isWaitingForResponse, autoSendMessage, consumeAutoSendMessage, dispatchMessageCb]);

  const fileUpload = useMemo<ChatBoxFileUpload>(() => {
    return {
      enabled: true,
      onSuccess: ({ file, url, bucket, key }) => {
        addToChatHistory({
          type: "user",
          data: { type: "markdown", message: "Added File: " + file.name },
        });
      },
    };
  }, []);

  const dismissedPlanIds = useRangerChatStore(s => s.dismissedPlanIds);
  const activePlanIds = useMemo(() => {
    const seen = new Set<number>();
    for (const msg of chatHistory) {
      if (msg.type !== "agent") continue;
      const tools: ToolCallEntry[] =
        (msg.data as ToolsReactMessage)?.allTools ??
        (msg.data as MarkdownWithToolsMessage)?.tools ??
        [];
      for (const t of tools) {
        if (
          ((t.tool === ChatToolType.SiriusMcp && t.toolName === "stageMutationPlan") ||
            t.tool === ChatToolType.StageMutationsTool ||
            t.tool === ChatToolType.ModifyMutationPlanTool) &&
          t.results?.planId
        ) {
          if (!dismissedPlanIds.has(t.results.planId)) {
            seen.add(t.results.planId);
          }
        }
      }
    }
    return [...seen];
  }, [chatHistory, dismissedPlanIds]);

  const stagedPlanBanner = activePlanIds.length > 0 ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 12px",
        backgroundColor: "#f0f7ff",
        borderTop: "1px solid #d6e4f0",
        fontSize: 12,
        color: "#1976d2",
        cursor: "pointer",
        flexShrink: 0,
      }}
      onClick={() => {
        const el = document.querySelector(`[data-mutation-plan-id="${activePlanIds[activePlanIds.length - 1]}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }}
    >
      <span style={{ fontWeight: 500 }}>
        {activePlanIds.length === 1
          ? "1 mutation plan staged"
          : `${activePlanIds.length} mutation plans staged`}
      </span>
      <span style={{ opacity: 0.7 }}>— click to view</span>
    </div>
  ) : null;

  const menuOpen = useRangerChatStore(s => s.menuOpen);

  const chatComponent = (
    <ChatComponent
      chatStoreId={RangerChatStoreId}
      isFullscreen={isFullscreen}
      chatMessages={[
        {
          type: "agent" as const,
          data: {
            type: "markdown" as const,
            message: `Welcome to Sirius AI! Our intelligent
                assistant uses context and tools to answer
                questions. Please ask me anything!`,
          },
        },
        ...chatHistory,
        ...(isWaitingForResponse
          ? [
              {
                type: "agent" as const,
                data: {
                  type: "react" as const,
                  message: (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {/* Inline "Working…" spinner. The antd <Spin> this replaced
                          drew its dots from the theme, so the old `color:"white"`
                          wrapper style never hid it; MUI's CircularProgress honors
                          `color`, so white made it invisible on the white message
                          list. Use the theme primary so it actually shows. */}
                      <CircularProgress size={16} color="primary" />
                      <span style={{ fontSize: 12, opacity: 0.7 }}>
                        {activityLabel}
                      </span>
                      {queuedMessage && !isCompressing && !isEditingContext && (
                        <span style={{ fontSize: 11, opacity: 0.6 }}>
                          · Next message queued (↑ to edit)
                        </span>
                      )}
                    </span>
                  ),
                },
              },
            ]
          : []),
      ]}
      agentWorking={isWaitingForResponse}
      cancelAgentFocus={cancelAgentFocus}
      aboveInput={stagedPlanBanner}
      knowledgeBase={
        knowledgeBaseId && knowledgeBaseData?.savedKnowledgeBase
          ? {
              id: knowledgeBaseId,
              name: knowledgeBaseData.savedKnowledgeBase.name,
              status: knowledgeBaseData.savedKnowledgeBase.status,
              noteCount: knowledgeBaseData.savedKnowledgeBase.noteCount || 0,
              documentCount:
                knowledgeBaseData.savedKnowledgeBase.documentCount || 0,
              investmentCount:
                knowledgeBaseData.savedKnowledgeBase.investmentCount || 0,
              firmCount: knowledgeBaseData.savedKnowledgeBase.firmCount || 0,
              companyCount:
                knowledgeBaseData.savedKnowledgeBase.companyCount || 0,
              onRemove: () => setKnowledgeBaseId(null),
              onRefresh: () => refetchKnowledgeBase(),
            }
          : undefined
      }
      handleUserMessage={async (
        text?: string,
        files?: ChatComponentFiles[]
      ) => {
        // Prevent sending empty messages
        if (!userText?.trim() && (!files || files.length === 0)) {
          return;
        }

        const messageText = userText;
        setUserText("");

        // If agent is already working, append to the queued message
        if (isWaitingForResponse) {
          appendToQueuedMessage(messageText);
          addToChatHistory({
            type: "user",
            data: { type: "markdown", message: messageText },
          });
          return;
        }

        dispatchMessageCb(messageText, files);
      }}
      controlButtons={<RangerChatMenuButton />}
      fileUpload={fileUpload}
      {...{ userText, setUserText }}
      agentCharacter={{
        name: "Sirius AI",
      }}
      onNewChat={() => {
        newChat();
      }}
      onRecallQueued={() => {
        const msg = editQueuedMessage();
        if (msg) {
          setUserText(msg);
          setChatHistory((history: ChatMessage[]) => withoutQueuedMessage(history, msg));
        }
      }}
    />
  );

  const slotWatchers = (
    <>
      {watchedSessions.map(sid => (
        <SlotWatcher key={sid} sessionId={sid} />
      ))}
      <ChatNotificationPrompt />
    </>
  );

  // Settings replace the conversation rather than floating over it. The slot
  // watchers below are deliberately NOT swapped out: they hold the live
  // subscriptions, so unmounting them while someone is in settings would drop
  // an in-flight response.
  const panelBody = menuOpen ? (
    <RangerChatSettings availableToolsDisplay={availableToolsDisplay} />
  ) : (
    chatComponent
  );

  if (isFullscreen) {
    return (
      <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
        {slotWatchers}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
          }}
        >
          {/* Fill the available width (the Conversation History panel already claims
              its own 300px); a maxWidth here left the expanded chat capped mid-screen. */}
          <div style={{ width: "100%" }}>{panelBody}</div>
        </div>
        {/* The standing history rail belongs to the CONVERSATION view. With
            settings open it would sit beside the settings' own History tab —
            two conversation histories on screen at once — and it would squeeze
            the settings pane the diff viewer needs. Settings replace the chat,
            rail included. */}
        {!menuOpen && (
          <div
            style={{
              width: 300,
              borderLeft: "1px solid #e8e8e8",
              padding: "12px",
              overflowY: "auto",
              flexShrink: 0,
            }}
          >
            <Typography.Text
              strong
              style={{ fontSize: 14, marginBottom: 8, display: "block" }}
            >
              Conversation History
            </Typography.Text>
            <ConversationHistoryTab />
          </div>
        )}
      </div>
    );
  }

  return <>{slotWatchers}{panelBody}</>;
}
